import { useEffect, useState } from "react";
import api from "../lib/api";
import { TASK_CODES } from "./forms/Form4CarePlan";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MOBILITY = ["125", "126", "128", "145"];

function Row({ label, children, testid }) {
  if (!children || (Array.isArray(children) && !children.length)) return null;
  return (
    <div className="grid grid-cols-[180px_1fr] gap-2 py-1.5" data-testid={testid}>
      <span className="text-xs font-semibold uppercase tracking-wide text-stone-400">{label}</span>
      <span className="whitespace-pre-wrap text-sm text-stone-800">{children}</span>
    </div>
  );
}

// Read-only display of the full service plan (Form 4) so the plan is visible on the client
// page without opening the edit wizard. Service start = first date of service from the visit log.
export default function ServicePlanSummary({ client }) {
  const cp = client.forms?.careplan || null;
  const start = (cp?.effective_date || client.service_start_date || "").trim();
  const [pattern, setPattern] = useState(null);
  useEffect(() => {
    if (!client.id) return;
    api.get(`/clients/${client.id}/visit-pattern`).then((r) => setPattern(r.data.pattern)).catch(() => setPattern(null));
  }, [client.id]);
  if (!cp && !start) return null;
  const tasks = (cp?.tasks || []).filter((t) => t.selected);
  const sched = DAYS.map((d) => [d, cp?.schedule?.[d]]).filter(([, v]) => v?.start && v?.end);
  const taskName = (code) => TASK_CODES.find((t) => t.code === code)?.name || code;
  const sel = (code) => tasks.some((t) => t.code === code);
  const confColor = { high: "bg-[#27AE60]/10 text-[#1e8449]", medium: "bg-[#B7791F]/10 text-[#8a5d14]", low: "bg-stone-100 text-stone-500" };
  const patternLine = pattern && pattern.total > 0 && (
    <p className="mt-2 text-xs text-stone-500" data-testid="sp-observed-schedule">
      Observed from visit history:{" "}
      <span className="font-medium text-stone-700">
        {pattern.days.length === 7 ? "every day" : (pattern.days.length ? pattern.days.join(" · ") : "no consistent days")}{pattern.span ? `, about ${pattern.span}` : ""}
      </span>{" "}
      <span className={`rounded-full px-1.5 py-px text-[9px] font-bold uppercase ${confColor[pattern.confidence]}`} data-testid="sp-schedule-confidence">
        {pattern.confidence} confidence
      </span>
      <span className="text-stone-400"> ({pattern.total.toLocaleString()} visits, {(pattern.span_share * 100).toFixed(0)}% within ~1 hr of usual times)</span>
    </p>
  );
  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="service-plan-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Service plan</h2>
        <p className="text-sm text-stone-600" data-testid="service-plan-start">
          Service start: <span className="font-semibold text-stone-900">{start || "not recorded"}</span>
        </p>
      </div>
      {!cp ? (
        <div>
          <p className="mt-3 text-xs italic text-stone-400" data-testid="service-plan-incomplete">
            Full service plan not completed yet — the start date above comes from the visit history. Open Form 4 below to complete the plan; the date prefills.
          </p>
          {patternLine}
        </div>
      ) : (
        <div className="mt-3 divide-y divide-stone-100" data-testid="service-plan-details">
          <div>
            <Row label="Authorization" testid="sp-auth">{[cp.auth_start, cp.auth_end].filter(Boolean).join(" → ")}</Row>
            <Row label="Authorized hours" testid="sp-hours">{cp.authorized_hours && `${cp.authorized_hours} per ${(cp.hours_period || "week").toLowerCase()}`}</Row>
            <Row label="Supervisor" testid="sp-supervisor">{cp.supervisor}</Row>
            <Row label="Caregiver(s)" testid="sp-caregivers">{cp.caregivers}</Row>
            <Row label="Needs" testid="sp-need">{cp.need}</Row>
            <Row label="Goal" testid="sp-goal">{cp.goal}</Row>
            <Row label="Intake summary" testid="sp-verbal">{cp.verbal_summary}</Row>
          </div>
          {sched.length === 0 && patternLine && (
            <div className="py-2" data-testid="sp-schedule">
              <p className="py-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">Weekly schedule</p>
              {patternLine}
            </div>
          )}
          {sched.length > 0 && (
            <div className="py-2" data-testid="sp-schedule">
              <p className="py-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">
                Weekly schedule{cp.total_weekly_hours ? ` · ${cp.total_weekly_hours} hrs` : ""}
              </p>
              <div className="flex flex-wrap gap-2">
                {sched.map(([d, v]) => (
                  <span key={d} className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-700" data-testid={`sp-sched-${d.toLowerCase()}`}>
                    {d.slice(0, 3)} {v.start}–{v.end}
                  </span>
                ))}
              </div>
              {cp.schedule_instructions && <p className="mt-2 text-xs text-stone-500">{cp.schedule_instructions}</p>}
              {patternLine}
            </div>
          )}
          {tasks.length > 0 && (
            <div className="py-2" data-testid="sp-tasks">
              <p className="py-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">Task codes</p>
              <div className="space-y-1.5">
                {tasks.map((t) => (
                  <p key={t.code} className="text-sm text-stone-800" data-testid={`sp-task-${t.code}`}>
                    <span className="font-semibold">{t.code} {taskName(t.code)}</span>
                    {(t.levels || []).length > 0 && <span className="text-stone-500"> — {t.levels.join(", ")}</span>}
                    {t.frequency && <span className="text-stone-500"> · {t.frequency}</span>}
                    {t.instruction && <span className="block pl-4 text-xs italic text-stone-500">{t.instruction}</span>}
                  </p>
                ))}
              </div>
            </div>
          )}
          {(sel("118") || sel("120") || tasks.some((t) => MOBILITY.includes(t.code))) && (
            <div>
              {sel("118") && (
                <>
                  <Row label="Medication reminders" testid="sp-med-times">{[cp.med_box_location && `box: ${cp.med_box_location}`, cp.reminder_times].filter(Boolean).join(" · ")}</Row>
                  <Row label="Med instructions" testid="sp-med-instr">{cp.reminder_instructions}</Row>
                  <Row label="If refused/missing" testid="sp-med-refused">{cp.med_refused_action}</Row>
                </>
              )}
              {sel("120") && (
                <Row label="Transportation" testid="sp-transport">
                  {[cp.transport_approved && `approved: ${cp.transport_approved}`, cp.transport_vehicle, cp.transport_instructions].filter(Boolean).join(" · ")}
                </Row>
              )}
              {tasks.some((t) => MOBILITY.includes(t.code)) && (
                <>
                  <Row label="Equipment" testid="sp-equipment">{cp.equipment_used}</Row>
                  <Row label="Transfer instructions" testid="sp-transfer">{cp.transfer_instructions}</Row>
                </>
              )}
            </div>
          )}
          <div>
            <Row label="Routines & preferences" testid="sp-routines">{cp.routines_preferences}</Row>
            <Row label="Off-limits" testid="sp-offlimits">{cp.off_limits}</Row>
            <Row label="Safety instructions" testid="sp-safety">{cp.safety_instructions}</Row>
            <Row label="Backup plan" testid="sp-backup">
              {[...(cp.backup_options || []), cp.backup_instructions].filter(Boolean).join(" · ")}
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}
